import { useCallback } from 'react';
import styled from 'styled-components';
import { rgba } from 'polished';
import * as Select from '@radix-ui/react-select';

import ChevronDownIcon from '#/assets/ChevronDownIcon';

import { c14142E, c7A35CB, cFFFFFF } from '#/colors';

import Field from '#/components/shared/Field';
import Tooltip, { HelpUnderline } from '#/components/shared/Tooltip';

import {
  useOrderFormActions,
  useOrderFormView,
} from '#/features/perpetuals/order-builder/form-context';

import useLabel from '#/utils/useLabel';
import { OrderTimeInForce } from '#/features/perpetuals/order-builder/form-state';
import { useTranslation } from '#/features/localization/utils';

export default function TimeInForceSelect() {
  const [label, labelId] = useLabel('Time in Force');
  const formActions = useOrderFormActions();
  const formView = useOrderFormView();

  const onValueChange = useCallback(
    (value: OrderTimeInForce | '') => {
      // For some reason the callback is called with an empty string
      // right after being called with the value selected by the user.
      if (value === '') return;
      formActions.updateTimeInForce(value);
    },
    [formActions],
  );

  return (
    <Field
      label={
        <Tooltip label={<Explanation />}>
          <HelpUnderline>TIF</HelpUnderline>
        </Tooltip>
      }
      labelId={labelId}
      mode="compact"
    >
      <Select.Root
        value={formView.timeInForce.value}
        onValueChange={onValueChange}
        disabled={formView.timeInForce.isDisabled}
      >
        <SelectTrigger
          className="SelectTrigger"
          aria-label={label}
          id={labelId}
        >
          <Select.Value>{formView.timeInForce.value}</Select.Value>
          <Select.Icon className="SelectIcon">
            <ChevronDownIcon />
          </Select.Icon>
        </SelectTrigger>
        <SelectContent
          className="SelectContent"
          position="popper"
          align="end"
          sideOffset={7}
        >
          <Select.Viewport className="SelectViewport">
            {formView.timeInForce.options.map((option) => (
              <Select.Item
                className="SelectItem"
                key={option.value}
                value={option.value}
                disabled={option.selected}
              >
                {option.value}
              </Select.Item>
            ))}
          </Select.Viewport>
        </SelectContent>
      </Select.Root>
    </Field>
  );
}

const ExplanationContent = () => {
  const { t } = useTranslation();

  return (
    <>
      <dt css="font-size: 11px;">
        <strong>TIF:</strong> Time In Force
      </dt>
      <dt>
        <strong>GTC:</strong> Good Till Canceled
      </dt>
      <dd>
        {t('Your order stays open until it is filled or you decide to cancel.')}
      </dd>
      <dt>
        <strong>IOC:</strong> Immediate or Cancel
      </dt>
      <dd>
        {t(
          'Your order gets filled right away, partially or fully, and whatever is left gets canceled instantly.',
        )}
      </dd>
    </>
  );
};

const Explanation = styled.dl.attrs({
  children: <ExplanationContent />,
})`
  padding: 4px;
  margin: 0;
  display: grid;
  line-height: 12px;
  gap: 8px;
  font-size: 10px;

  dt {
    padding: 0;
    margin: 0;
    color: ${rgba(cFFFFFF, 0.7)};
    strong {
      color: ${cFFFFFF};
    }
  }

  dd {
    padding: 0;
    margin: 0;
    color: ${rgba(cFFFFFF, 0.5)};
  }
`;

const SelectTrigger = styled(Select.Trigger)`
  all: unset;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border-radius: 4px;
  font-size: 12px;
  gap: 5px;
  color: ${cFFFFFF};
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${rgba(c7A35CB, 0.5)};
    outline-offset: 2px;
  }

  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .SelectIcon {
    display: grid;
    align-items: center;
    color: ${rgba(cFFFFFF, 0.7)};
  }

  &[data-state='open'] .SelectIcon {
    transform: rotate(180deg);
  }
`;

const SelectContent = styled(Select.Content)`
  background-color: ${c14142E};
  border-radius: 7px;

  /* .SelectViewport {
    padding: 0;
  } */

  .SelectItem {
    font-size: 12px;
    line-height: 1;
    color: ${rgba(cFFFFFF, 0.5)};
    border-radius: 7px;
    display: flex;
    align-items: center;
    height: 28px;
    padding: 12px 19px;
    position: relative;
    user-select: none;
    cursor: pointer;
  }
  .SelectItem[data-highlighted] {
    outline: none;
    color: ${cFFFFFF};
  }
  .SelectItem[data-disabled] {
    background-color: ${c7A35CB};
    color: ${cFFFFFF};
    cursor: default;
  }

  .SelectLabel {
    padding: 0 25px;
    font-size: 12px;
    line-height: 25px;
    color: green;
  }

  .SelectSeparator {
    height: 1px;
    background-color: purple;
    margin: 5px;
  }

  .SelectItemIndicator {
    position: absolute;
    left: 0;
    width: 25px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .SelectScrollButton {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 25px;
    background-color: white;
    color: purple;
    cursor: default;
  }
`;
